// Layout.jsx with @tippyjs/react tooltips
import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate, NavLink, Link } from 'react-router-dom';
import { useUserMenus, buildMenuTree } from '../context/useUserMenus';
import { useAuth } from '../context/AuthContext';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import {
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Bars3Icon,
  MoonIcon,
  SunIcon,
  HomeIcon,
  ChartBarIcon,
  CogIcon,
  UsersIcon,
  ShoppingCartIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  PlusCircleIcon,
  ListBulletIcon,
  DocumentTextIcon,
  BanknotesIcon,
  GiftIcon,
  UserGroupIcon,
  MinusCircleIcon,
  ArrowUpOnSquareIcon,
  ClipboardDocumentIcon,
  LightBulbIcon,
  CurrencyBangladeshiIcon,
  PlusIcon,
  SquaresPlusIcon,
  StarIcon,
  UserCircleIcon,
  BugAntIcon,
  CakeIcon,
  BriefcaseIcon,
  AtSymbolIcon,
  SignalIcon,
  CloudIcon,
  ReceiptPercentIcon
} from '@heroicons/react/24/outline';

const iconMap = {
  trash: TrashIcon,
  home: HomeIcon,
  chart: ChartBarIcon,
  settings: CogIcon,
  users: UsersIcon,
  sales: ShoppingCartIcon,
  purchases: ShoppingBagIcon,
  money: CurrencyDollarIcon,
  plus: PlusCircleIcon,
  plusCircle: PlusIcon,
  list: ListBulletIcon,
  report: DocumentTextIcon,
  bank: BanknotesIcon,
  items: GiftIcon,
  customers: UserGroupIcon,
  expenses: MinusCircleIcon,
  upload: ArrowUpOnSquareIcon,
  clipboard: ClipboardDocumentIcon,
  info: LightBulbIcon,
  taka: CurrencyBangladeshiIcon,
  dollar: CurrencyDollarIcon,
  supplier: SquaresPlusIcon,
  profit: Bars3Icon,
  unsold: StarIcon,
  user: UserCircleIcon,
  stock: BugAntIcon,
  cake: CakeIcon,
  briefcase: BriefcaseIcon,
  at: AtSymbolIcon,
  size: SignalIcon,
  cloud: CloudIcon,
  profit2: ReceiptPercentIcon,
};

export default function Layout({ children }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const sidebarRef = useRef();
  const { logout } = useAuth();
  const { menus, loading } = useUserMenus(user?.id);
  const visibleMenus = menus.filter(menu => menu.show_in_menu);
  const sidebarMenus = buildMenuTree(visibleMenus);

  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [theme, setTheme] = useState("light");
  const [openMenus, setOpenMenus] = useState({});

  useEffect(() => {
    setCollapsed(localStorage.getItem("sidebarCollapsed") === "true");
    const savedTheme = localStorage.getItem("theme") || "light";
    setTheme(savedTheme);
    document.documentElement.classList.toggle("dark", savedTheme === "dark");
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false); // close sidebar
      }
    };

    if (sidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarOpen]);
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    localStorage.setItem("theme", next);
    document.documentElement.classList.toggle("dark", next === "dark");
  };

  const toggleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", newState);
  };

  const toggleMenu = (label) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const handleNavClick = () => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  const renderSidebar = (items) => (
    <div className="overflow-y-auto h-full pr-1 mt-2 pb-5 mb-5">
      {items.map((item) => (
        <div key={item.id}>
          {item.children.length > 0 ? (
            <>
              <Tippy content={item.label} disabled={!collapsed} placement="right">
                <div
                  className="flex justify-between items-center px-4 py-2 cursor-pointer hover:bg-[#000042] rounded"
                  onClick={() => toggleMenu(item.label)}
                >
                  <div className="flex items-center gap-2">
                    {(() => {
                      const Icon = iconMap[item.icon] || DocumentTextIcon;
                      return <Icon className="w-5 h-5" />;
                    })()}
                    {!collapsed && <span>{item.label}</span>}
                  </div>
                  {!collapsed && (openMenus[item.label] ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />)}
                </div>
              </Tippy>
              <div className={`overflow-hidden transition-all ${openMenus[item.label] ? 'max-h-[400px]' : 'max-h-0'}`}>
                <div className="ml-6 mt-1 space-y-1">
                  {item.children.map((child) => (
                    <Tippy content={child.label} disabled={!collapsed} placement="right" key={child.id}>
                      <NavLink
                        to={child.path}
                        onClick={handleNavClick}
                        className={({ isActive }) =>
                          `flex items-center gap-2 px-3 py-1 text-sm rounded hover:bg-[#000042] ${isActive ? "bg-[#000042] font-semibold border-l-2 border-white" : ""}`
                        }
                      >
                        {(() => {
                          const Icon = iconMap[child.icon] || DocumentTextIcon;
                          return <Icon className="w-4 h-4" />;
                        })()}
                        {!collapsed && <span>{child.label}</span>}
                      </NavLink>
                    </Tippy>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <Tippy content={item.label} disabled={!collapsed} placement="right">
              <NavLink
                to={item.path}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded hover:bg-[#000042] transition ${isActive ? "bg-[#000042] font-bold border-l-4 border-white" : ""}`
                }
              >
                {(() => {
                  const Icon = iconMap[item.icon] || DocumentTextIcon;
                  return <Icon className="w-5 h-5" />;
                })()}
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            </Tippy>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200">
      <header className="bg-white dark:bg-gray-800 shadow px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <button className="md:hidden" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Bars3Icon className="w-6 h-6" />
          </button>
          <Link className="text-xl font-bold text-[#001b72]" to='/dashboard'>Grab For You</Link>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={toggleTheme}>
            {theme === "light" ? <MoonIcon className="w-5 h-5" /> : <SunIcon className="w-5 h-5" />}
          </button>
          <img
            src={`https://ui-avatars.com/api/?name=${user?.name || 'User'}`}
            alt="avatar"
            className="w-8 h-8 rounded-full cursor-pointer"
            onClick={handleLogout}
          />
        </div>
      </header>

      <div className="flex flex-1 bg-[#001b72]">
        <aside ref={sidebarRef} className={`bg-[#001b72] text-white py-6 px-2 absolute md:relative z-20 transition-all duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"} ${collapsed ? "w-16" : "w-64"} h-screen`}>
          <div className="flex justify-between items-center px-2">
            {!collapsed && <span className="text-lg font-bold">Menu</span>}
            <button onClick={toggleCollapse} className="text-white">
              {collapsed ? <ChevronRightIcon className="w-5 h-5" /> : <ChevronLeftIcon className="w-5 h-5" />}
            </button>
          </div>
          {loading ? <p className="text-center mt-6">Loading...</p> : renderSidebar(sidebarMenus)}
        </aside>

        <main className="flex-1 bg-gray-100 dark:bg-[#000042] p-4 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}